import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import { SelectOption } from '@chiroup/components/form/Select';
import { getId } from '@chiroup/core/functions/getId';
import { DownloadAgingResponse, RawAgingResponse } from '@chiroup/core/types/ARAging.type';
import {
  ClaimRejectionsDownloadResponse,
  ClaimRejectionsRequest,
  ClaimRejectionsResponse,
  ClaimRejectionWorkedRequest,
} from '@chiroup/core/types/ClaimRejections.type';
import { Condition } from '@chiroup/core/types/Condition.type';
import { RecallMessageTemplate } from '@chiroup/core/types/PatientRecall.type';
import {
  ARAgingReportFilters,
  CompensationCalculatorReportFilters,
  DiscountDetailReportFilters,
  MembershipsReportFilters,
  NewPatientsReportFilters,
  PackagesReportFilters,
  PatientCreditReportFilters,
  PatientSummaryFilters,
  PaymentCollectionReportFilters,
  PaymentDetailReportFilters,
  PurchaseDetailReportFilters,
  RecallBoardFilters,
  RecurringSchedulesReportFilters,
  ScheduledPatientsReportFilters,
  ServiceCodeUsageReportFilters,
  SupplementSupplySalesReportFilters,
  UnscheduledPatientsReportFilters,
} from '@chiroup/core/types/ReportingFilters.type';
import {
  ReportSnapshotId,
  SnapshotGenerateResponse,
  SnapshotLoadResponse,
} from '@chiroup/core/types/ReportSnapshot.type';
import {
  SupplementSupplySalesDownloadResponse,
  SupplementSupplySalesResponse,
  SupplementSupplySalesSnapshotListEntry,
  SupplementSupplySalesSnapshotRequest,
} from '@chiroup/core/types/SupplementSupplySales.type';

const reportService = () => {
  const listRegionConditions = async (
    clinicId: number
  ): Promise<{ [key: string]: SelectOption[] }> => {
    return ChiroUpAPI.get('api', `/${clinicId}/conditions`, {
      queryParams: {
        grouped: true,
        regions: 'C,S,E,H/W,T,L,H,K,F/A,F/B,O,HEAD',
        hide: true,
      },
    });
  };

  const listConditions = async (
    clinicId: number,
    hide?: boolean,
    basicList?: boolean
  ): Promise<Condition[]> => {
    return await ChiroUpAPI.get('api', `/${clinicId}/conditions`, {
      queryParams: {
        hide,
        basicList,
      },
    });
  };

  const getConditionDetails = async (
    clinicId: number,
    userId: string,
    conditions: number[],
    mine?: boolean,
    lightweight?: boolean
  ): Promise<{ data: Partial<Condition>[] }> => {
    const queryParams: any = {};
    if (!conditions?.length) return { data: [] };

    if (mine) {
      queryParams.version = 'mine';
    }

    if (lightweight) {
      queryParams.lightweight = 'true';
    }

    const res: Partial<Condition>[] = await ChiroUpAPI.get(
      'api',
      `/${clinicId}/condition/${conditions.join(',')}`,
      queryParams
        ? {
            queryParams: queryParams,
          }
        : {}
    );

    const formattedConditions = (res || []).map(condition => {
      const formatted: Partial<Condition> = { ...condition };

      // Only format exercise if it exists in the response (lightweight responses omit this property)
      if (condition.exercise !== undefined && condition.exercise !== null) {
        formatted.exercise = condition.exercise.map(exer => ({
          ...exer,
          $id: getId(exer),
        }));
      } else {
        // Remove exercise property if it doesn't exist to preserve lightweight response structure
        delete formatted.exercise;
      }

      return formatted;
    });

    return { data: formattedConditions };
  };

  const saveReleaseReport = (
    clinicId: number | undefined,
    userId: string | undefined,
    conditionReportId: string,
    val: any,
    existing: boolean
  ) => {
    if (existing) {
      return ChiroUpAPI.patch(
        'api',
        `/patients-communications/${clinicId}/report/release/${conditionReportId}`,
        { body: val }
      );
    } else {
      return ChiroUpAPI.post(
        'api',
        `/patients-communications/${clinicId}/report/release/${conditionReportId}`,
        { body: val }
      );
    }
  };

  const list = async (
    params: { search?: string; limit?: number; lastKey?: string },
    selectedClinic?: number
  ) => {
    const res: { data: any[]; lastKey: string } = await ChiroUpAPI.get(
      'api',
      `/${selectedClinic}/reports`,
      {
        queryParams: params,
      }
    );

    return {
      ...res,
      data: (res.data || []).filter(pt => !pt.disabled),
    };
  };

  const arAging = async ({
    clinicId,
    params,
  }: {
    clinicId: number;
    params: ARAgingReportFilters & { skipSummary?: boolean };
  }): Promise<RawAgingResponse> => {
    const queryParams: Record<string, any> = {
      ...params,
    };

    if (!queryParams.excludeSupervisedItems) {
      delete queryParams.excludeSupervisedItems;
    }

    return ChiroUpAPI.get('api', `/reporting/${clinicId}/billing/ar-aging`, {
      queryParams,
    });
  };

  // Method to fetch top patients on demand
  const arAgingTopPatients = async ({
    clinicId,
    params,
  }: {
    clinicId: number;
    params: ARAgingReportFilters;
  }) => {
    const queryParams: Record<string, any> = {
      ...params,
      queryType: 'topPatients',
    };

    if (!queryParams.excludeSupervisedItems) {
      delete queryParams.excludeSupervisedItems;
    }

    return await ChiroUpAPI.get('api', `/reporting/${clinicId}/billing/ar-aging`, {
      queryParams,
    });
  };

  // Method to fetch top payors on demand
  const arAgingTopPayors = async ({
    clinicId,
    params,
  }: {
    clinicId: number;
    params: ARAgingReportFilters;
  }) => {
    const queryParams: Record<string, any> = {
      ...params,
      queryType: 'topPayors',
    };

    if (!queryParams.excludeSupervisedItems) {
      delete queryParams.excludeSupervisedItems;
    }

    return await ChiroUpAPI.get('api', `/reporting/${clinicId}/billing/ar-aging`, {
      queryParams,
    });
  };

  const fetchArAgingInvoices = async ({
    clinicId,
    filters,
  }: {
    clinicId: number;
    filters: ARAgingReportFilters;
  }) => {
    const queryParams: ARAgingReportFilters = {};
    // Define an array of filter keys to process
    const filterKeys: Array<keyof ARAgingReportFilters> = [
      'daysOutstanding',
      'providerIds',
      'locationIds',
      'caseTypeIds',
      'patientId',
      'payorId',
      'billingPriority',
      'amountMode',
      'dateStart',
      'claimStatuses',
    ];

    // Process each filter key in a type-safe way
    filterKeys.forEach(key => {
      const value = filters[key];

      if (value !== undefined && value !== null) {
        if (typeof value === 'boolean' && value === false) {
          return;
        }

        // Use type assertion to ensure TypeScript understands this assignment is valid
        (queryParams as any)[key] = value;
      }
    });

    const patientId = queryParams.patientId ?? 'any';

    return ChiroUpAPI.get('api', `/reporting/${clinicId}/billing/ar-aging/${patientId}`, {
      queryParams,
    });
  };

  const downloadAging = async ({
    clinicId,
    params,
  }: {
    clinicId: number;
    params: ARAgingReportFilters & { download: true };
  }): Promise<DownloadAgingResponse> => {
    return ChiroUpAPI.get('api', `/reporting/${clinicId}/billing/ar-aging`, {
      queryParams: params,
    });
  };

  const compensationCalculator = async ({
    params,
    clinicId,
  }: {
    params: CompensationCalculatorReportFilters;
    clinicId: number;
  }): Promise<{ data: any[]; count: number; skip: number }> => {
    const queryParams: CompensationCalculatorReportFilters = {
      ...params,
      minify: true,
    };

    if (!queryParams.excludeSupervisedItems) {
      delete queryParams.excludeSupervisedItems;
    }

    const res = await ChiroUpAPI.get(
      'api',
      `/reporting/${clinicId}/billing/compensation-calculator`,
      {
        queryParams,
      }
    );

    // When the payload exceeds Lambda's 6MB limit, the backend uploads
    // to S3 and returns a presigned URL instead of the data directly.
    if (res?.signedUrl) {
      const s3Res = await fetch(res.signedUrl);

      return s3Res.json();
    }

    return res;

    // return ChiroUpJSON.expand(res);
  };

  const purchaseDetailReport = async ({
    params,
    clinicId,
  }: {
    params: PurchaseDetailReportFilters;
    clinicId: number;
  }): Promise<{ data: any[]; count: number; skip: number }> => {
    const queryParams: PurchaseDetailReportFilters = {
      ...params,
    };

    if (!queryParams.excludeSupervisedItems) {
      delete queryParams.excludeSupervisedItems;
    }

    return ChiroUpAPI.get('api', `/reporting/${clinicId}/billing/purchase-detail`, {
      queryParams,
    });
  };

  const scheduledPatients = async ({
    params,
    clinicId,
  }: {
    params: ScheduledPatientsReportFilters;
    clinicId: number;
  }): Promise<{ data: any[]; count: number; skip: number }> => {
    return ChiroUpAPI.get('api', `/reporting/${clinicId}/scheduling/scheduled-patients`, {
      queryParams: params,
    });
  };

  const unscheduledPatients = async ({
    params,
    clinicId,
  }: {
    params: UnscheduledPatientsReportFilters;
    clinicId: number;
  }): Promise<{ data: any[]; count: number; skip: number }> => {
    return ChiroUpAPI.get('api', `/reporting/${clinicId}/scheduling/unscheduled-patients`, {
      queryParams: params,
    });
  };

  const newPatients = async ({
    params,
    clinicId,
  }: {
    params: NewPatientsReportFilters;
    clinicId: number;
  }): Promise<{ data: any[]; count: number; skip: number }> => {
    return ChiroUpAPI.get('api', `/reporting/${clinicId}/scheduling/new-patients`, {
      queryParams: params,
    });
  };

  const discountDetail = async ({
    params,
    clinicId,
  }: {
    params: DiscountDetailReportFilters;
    clinicId: number;
  }): Promise<{ data: any[]; count: number; skip: number }> => {
    return ChiroUpAPI.get('api', `/reporting/${clinicId}/billing/discount-detail`, {
      queryParams: params,
    });
  };

  const postedPayments = async ({
    params,
    clinicId,
  }: {
    params: PaymentDetailReportFilters;
    clinicId: number;
  }): Promise<{ data: any[]; count: number; skip: number }> => {
    Object.keys(params).forEach(key => {
      const obj = params as any;

      if (obj[key] === null || obj[key] === undefined || obj[key] === false) {
        delete obj[key];
      }
    });

    return ChiroUpAPI.get('api', `/reporting/${clinicId}/billing/posted-payments`, {
      queryParams: params,
    });
  };

  const paymentCollection = async ({
    params,
    clinicId,
  }: {
    params: PaymentCollectionReportFilters;
    clinicId: number;
  }): Promise<{ data: any[]; count: number; skip: number }> => {
    Object.keys(params).forEach(key => {
      const obj = params as any;

      if (obj[key] === null || obj[key] === undefined) {
        delete obj[key];
      }
    });

    return ChiroUpAPI.get('api', `/reporting/${clinicId}/billing/payment-collection`, {
      queryParams: params,
    });
  };

  const patientCredits = async ({
    params,
    clinicId,
  }: {
    params: PatientCreditReportFilters;
    clinicId: number;
  }): Promise<any> => {
    Object.keys(params).forEach(key => {
      const obj = params as any;

      if (obj[key] === null || obj[key] === undefined) {
        delete obj[key];
      }
    });

    return ChiroUpAPI.get('api', `/reporting/${clinicId}/billing/patient-credits`, {
      queryParams: params,
    });
  };

  /** Unset values stay off the URL: the API reads a missing param as no filter. */
  const claimRejectionsQueryParams = (params: ClaimRejectionsRequest): Record<string, string> =>
    Object.fromEntries(
      Object.entries(params)
        .filter(
          ([, value]) =>
            value !== undefined &&
            value !== null &&
            value !== '' &&
            value !== false &&
            !(Array.isArray(value) && !value.length)
        )
        .map(([key, value]) => [key, Array.isArray(value) ? value.join(',') : String(value)])
    );

  const claimRejections = async ({
    params,
    clinicId,
  }: {
    params: ClaimRejectionsRequest;
    clinicId: number;
  }): Promise<ClaimRejectionsResponse> => {
    return ChiroUpAPI.get('api', `/reporting/${clinicId}/billing/claim-rejections`, {
      queryParams: claimRejectionsQueryParams(params),
    });
  };

  const claimRejectionsDownload = async ({
    params,
    clinicId,
  }: {
    params: ClaimRejectionsRequest;
    clinicId: number;
  }): Promise<ClaimRejectionsDownloadResponse> => {
    return ChiroUpAPI.get('api', `/reporting/${clinicId}/billing/claim-rejections`, {
      queryParams: { ...claimRejectionsQueryParams(params), download: 'true' },
    });
  };

  const claimRejectionWorked = async ({
    clinicId,
    request,
  }: {
    clinicId: number;
    request: ClaimRejectionWorkedRequest;
  }): Promise<{ worked: boolean }> => {
    return ChiroUpAPI.post('api', `/reporting/${clinicId}/billing/claim-rejections/worked`, {
      body: request,
    });
  };

  const verifyPayments = async ({
    clinicId,
    paymentIds,
  }: {
    clinicId: number;
    paymentIds: string[];
  }) => {
    return ChiroUpAPI.post('api', `/reporting/${clinicId}/billing/payments-verify`, {
      body: paymentIds,
    });
  };

  const unVerifyPayments = async ({
    clinicId,
    paymentIds,
  }: {
    clinicId: number;
    paymentIds: string[];
  }) => {
    return ChiroUpAPI.post('api', `/reporting/${clinicId}/billing/payments-unverify`, {
      body: paymentIds,
    });
  };

  // --- Report Snapshot Methods ---
  //
  // One implementation per snapshot verb, shared by every report; the
  // per-report functions below bind nothing but a reportId. These call the
  // consolidated routes (/billing/reports/{reportId}/...). The old
  // report-specific paths are still served by the same Lambdas so an older
  // bundle keeps working, but nothing here targets them any more.

  const reportSnapshotList = async ({
    clinicId,
    reportId,
  }: {
    clinicId: number;
    reportId: ReportSnapshotId;
  }) => {
    return ChiroUpAPI.get(
      'api',
      `/reporting/${clinicId}/billing/reports/${reportId}/snapshots`,
      {}
    );
  };

  const reportSnapshotGenerate = async ({
    clinicId,
    reportId,
    params,
  }: {
    clinicId: number;
    reportId: ReportSnapshotId;
    params: Record<string, unknown>;
  }) => {
    return ChiroUpAPI.post(
      'api',
      `/reporting/${clinicId}/billing/reports/${reportId}/snapshot/generate`,
      { body: params }
    );
  };

  const reportSnapshotLoad = async ({
    clinicId,
    reportId,
    snapshotId,
  }: {
    clinicId: number;
    reportId: ReportSnapshotId;
    snapshotId: string;
  }) => {
    const res = await ChiroUpAPI.get(
      'api',
      `/reporting/${clinicId}/billing/reports/${reportId}/snapshot/${snapshotId}/load`,
      {}
    );

    // Large complete payloads come back as { status: 'complete', signedUrl } —
    // fetch the S3 JSON and re-wrap so the consumer sees a single shape.
    if (res?.status === 'complete' && res?.signedUrl) {
      const s3Res = await fetch(res.signedUrl);
      const payload = await s3Res.json();

      return { status: 'complete', ...payload };
    }

    return res;
  };

  const reportSnapshotDownload = async ({
    clinicId,
    reportId,
    snapshotId,
  }: {
    clinicId: number;
    reportId: ReportSnapshotId;
    snapshotId: string;
  }) => {
    return ChiroUpAPI.get(
      'api',
      `/reporting/${clinicId}/billing/reports/${reportId}/snapshot/${snapshotId}/download`,
      {}
    );
  };

  const paymentCollectionSnapshotList = async ({ clinicId }: { clinicId: number }) => {
    return reportSnapshotList({ clinicId, reportId: 'payment_collection' });
  };

  const paymentCollectionSnapshotGenerate = async ({
    clinicId,
    params,
  }: {
    clinicId: number;
    params: Record<string, any>;
  }) => {
    return reportSnapshotGenerate({ clinicId, reportId: 'payment_collection', params });
  };

  const paymentCollectionSnapshotLoad = async ({
    clinicId,
    snapshotId,
  }: {
    clinicId: number;
    snapshotId: string;
  }) => {
    return reportSnapshotLoad({ clinicId, reportId: 'payment_collection', snapshotId });
  };

  const paymentCollectionSnapshotDownload = async ({
    clinicId,
    snapshotId,
  }: {
    clinicId: number;
    snapshotId: string;
  }) => {
    return reportSnapshotDownload({ clinicId, reportId: 'payment_collection', snapshotId });
  };

  const unsignedSOAPNotes = ({ clinicId, params }: { clinicId: number; params: any }) => {
    const queryParams = {
      ...params,
    };

    if (!queryParams.excludeSupervisedItems) {
      delete queryParams.excludeSupervisedItems;
    }

    return ChiroUpAPI.get('api', `/reporting/${clinicId}/clinical/unsigned-soap-notes`, {
      queryParams,
    });
  };

  function buildQueryParams(params: PatientSummaryFilters) {
    const result: Record<string, string> = {};

    if (params.dobMonths && params.dobMonths.length) {
      result.dobMonths = params.dobMonths.join(',');
    }

    if (params.status && params.status.length) {
      const status = params.status;
      result.status = typeof status === 'string' ? status : status.join(',');
    }

    if (params.clinicianIds && params.clinicianIds.length) {
      const clinicianIds = params.clinicianIds;
      result.clinicianIds =
        typeof clinicianIds === 'string' ? clinicianIds : clinicianIds.join(',');
    }

    if (params.caseTypes && params.caseTypes.length) {
      const caseTypes = params.caseTypes;
      result.caseTypes = typeof caseTypes === 'string' ? caseTypes : caseTypes.join(',');
    }

    if (params.optedIn && params.optedIn.length) {
      const optedIn = params.optedIn;
      result.optedIn = typeof optedIn === 'string' ? optedIn : optedIn.join(',');
    }

    if (params.prefers && params.prefers.length) {
      const prefers = params.prefers;
      result.prefers = typeof prefers === 'string' ? prefers : prefers.join(',');
    }

    if (params.referralSources && params.referralSources.length) {
      const referralSources = params.referralSources;
      result.referralSources =
        typeof referralSources === 'string' ? referralSources : referralSources.join(',');
    }

    if (typeof params.skip === 'number') {
      result.skip = String(params.skip);
    }

    if (typeof params.limit === 'number') {
      result.limit = String(params.limit);
    }

    if (params.download) {
      result.download = 'true';
    }

    return result;
  }

  const patientSummary = async ({
    clinicId,
    params,
  }: {
    clinicId: number;
    params: PatientSummaryFilters;
  }) => {
    const response = await ChiroUpAPI.get('api', `/reporting/${clinicId}/clinical/patientSummary`, {
      queryParams: buildQueryParams(params),
    });

    return response;
  };

  const serviceCodeUsage = async ({
    params,
    clinicId,
  }: {
    params: ServiceCodeUsageReportFilters;
    clinicId: number;
  }): Promise<{ data: any[]; count: number; skip: number; stats: any }> => {
    const queryParams: Record<string, any> = { ...params };

    // Clean up falsy values
    Object.keys(queryParams).forEach(key => {
      const val = queryParams[key];

      if (val === null || val === undefined || val === false) {
        delete queryParams[key];
      }
    });

    return ChiroUpAPI.get('api', `/reporting/${clinicId}/billing/service-code-usage`, {
      queryParams,
    });
  };

  const supplementSupplySalesQueryParams = (params: SupplementSupplySalesReportFilters) => {
    const queryParams: Record<string, any> = { ...params };

    // Clean up falsy values
    Object.keys(queryParams).forEach(key => {
      const val = queryParams[key];

      if (val === null || val === undefined || val === false) {
        delete queryParams[key];
      }
    });

    return queryParams;
  };

  const supplementSupplySales = async ({
    params,
    clinicId,
  }: {
    params: SupplementSupplySalesReportFilters;
    clinicId: number;
  }): Promise<SupplementSupplySalesResponse> => {
    return ChiroUpAPI.get('api', `/reporting/${clinicId}/billing/supplement-supply-sales`, {
      queryParams: supplementSupplySalesQueryParams(params),
    });
  };

  /**
   * Same endpoint as `supplementSupplySales`, split out because `download: true`
   * returns the S3 handoff rather than report rows -- following `arAging` /
   * `downloadAging`, so neither caller has to narrow a union.
   */
  const supplementSupplySalesDownload = async ({
    params,
    clinicId,
  }: {
    params: SupplementSupplySalesReportFilters;
    clinicId: number;
  }): Promise<SupplementSupplySalesDownloadResponse> => {
    return ChiroUpAPI.get('api', `/reporting/${clinicId}/billing/supplement-supply-sales`, {
      queryParams: supplementSupplySalesQueryParams({ ...params, download: true }),
    });
  };

  const supplementSupplySalesSnapshotList = async ({
    clinicId,
  }: {
    clinicId: number;
  }): Promise<{ snapshots: SupplementSupplySalesSnapshotListEntry[] }> => {
    return reportSnapshotList({ clinicId, reportId: 'supplement_supply_sales' });
  };

  const supplementSupplySalesSnapshotGenerate = async ({
    clinicId,
    params,
  }: {
    clinicId: number;
    params: SupplementSupplySalesSnapshotRequest;
  }): Promise<SnapshotGenerateResponse> => {
    return reportSnapshotGenerate({ clinicId, reportId: 'supplement_supply_sales', params });
  };

  const supplementSupplySalesSnapshotLoad = async ({
    clinicId,
    snapshotId,
  }: {
    clinicId: number;
    snapshotId: string;
  }): Promise<SnapshotLoadResponse> => {
    return reportSnapshotLoad({ clinicId, reportId: 'supplement_supply_sales', snapshotId });
  };

  const supplementSupplySalesSnapshotDownload = async ({
    clinicId,
    snapshotId,
  }: {
    clinicId: number;
    snapshotId: string;
  }): Promise<SupplementSupplySalesDownloadResponse> => {
    return reportSnapshotDownload({ clinicId, reportId: 'supplement_supply_sales', snapshotId });
  };

  const insurancePolicy = ({ clinicId, params }: { clinicId: number; params: any }) => {
    return ChiroUpAPI.get('api', `/reporting/${clinicId}/billing/insurance-policy`, {
      queryParams: params,
    });
  };

  const recallBoard = async ({
    clinicId,
    params,
  }: {
    clinicId: number;
    params: RecallBoardFilters & { countHash?: string; previousCount?: number };
  }): Promise<{ data: any[]; count: number; countHash: string; skip: number }> => {
    return ChiroUpAPI.get('api', `/reporting/${clinicId}/scheduling/recall`, {
      queryParams: params,
    });
  };

  const recallContactHistory = async ({
    clinicId,
    patientId,
    params,
  }: {
    clinicId: number;
    patientId: string;
    params: { start: number; end: number; limit: number; offset: number };
  }): Promise<{ data: any[]; count: number }> => {
    return ChiroUpAPI.get('api', `/reporting/${clinicId}/scheduling/recall/${patientId}/history`, {
      queryParams: params,
    });
  };

  const recallPatientGet = async ({
    clinicId,
    patientId,
  }: {
    clinicId: number;
    patientId: string;
  }): Promise<any> => {
    return ChiroUpAPI.get('api', `/reporting/${clinicId}/scheduling/recall/${patientId}`, {});
  };

  const recallPatientUpdate = async ({
    clinicId,
    patientId,
    body,
  }: {
    clinicId: number;
    patientId: string;
    body: { recallDate: string };
  }): Promise<any> => {
    return ChiroUpAPI.put('api', `/reporting/${clinicId}/scheduling/recall/${patientId}`, { body });
  };

  const recallAttemptCreate = async ({
    clinicId,
    patientId,
    body,
  }: {
    clinicId: number;
    patientId: string;
    body: any;
  }): Promise<any> => {
    return ChiroUpAPI.post('api', `/reporting/${clinicId}/scheduling/recall/${patientId}/attempt`, {
      body,
    });
  };

  const recallMoveToReactivation = async ({
    clinicId,
    patientId,
    body,
  }: {
    clinicId: number;
    patientId: string;
    body: { recallDate: string };
  }): Promise<any> => {
    return ChiroUpAPI.post(
      'api',
      `/reporting/${clinicId}/scheduling/recall/${patientId}/move-to-reactivation`,
      { body }
    );
  };

  const recallRemove = async ({
    clinicId,
    patientId,
  }: {
    clinicId: number;
    patientId: string;
  }): Promise<any> => {
    return ChiroUpAPI.post('api', `/reporting/${clinicId}/scheduling/recall/${patientId}/remove`, {
      body: {},
    });
  };

  const recallNoHistoryList = async ({
    clinicId,
    params,
  }: {
    clinicId: number;
    params: {
      skip: number;
      limit: number;
      patientId?: string;
      patientStatus?: string;
      showDoNotRecall?: boolean;
      countHash?: string;
      previousCount?: number;
    };
  }): Promise<{ data: any[]; count: number; countHash: string; skip: number }> => {
    return ChiroUpAPI.get('api', `/reporting/${clinicId}/scheduling/recall/no-history`, {
      queryParams: params,
    });
  };

  const recallNoHistoryAddToRecall = async ({
    clinicId,
    body,
  }: {
    clinicId: number;
    body: {
      patientIds: string[];
      recallDate: number;
      skip: number;
      limit: number;
      patientId?: string;
      patientStatus?: string;
      showDoNotRecall?: boolean;
      recallBoardFilters?: any;
    };
  }): Promise<{
    noHistory: { data: any[]; count: number; countHash: string; skip: number };
    recallBoard?: {
      data: any[];
      count: number;
      countHash: string;
      skip: number;
      sectionCounts: Record<string, number>;
    };
  }> => {
    return ChiroUpAPI.post('api', `/reporting/${clinicId}/scheduling/recall/no-history`, { body });
  };

  const recallNoHistoryRemove = async ({
    clinicId,
    body,
  }: {
    clinicId: number;
    body: {
      removePatientId: string;
      skip: number;
      limit: number;
      patientId?: string;
      patientStatus?: string;
      showDoNotRecall?: boolean;
    };
  }): Promise<{ data: any[]; count: number; countHash: string; skip: number }> => {
    return ChiroUpAPI.post('api', `/reporting/${clinicId}/scheduling/recall/no-history/remove`, {
      body,
    });
  };

  const recallSettingsGet = async ({ clinicId }: { clinicId: number }): Promise<any> => {
    return ChiroUpAPI.get('api', `/reporting/${clinicId}/scheduling/recall-settings`, {});
  };

  const recallSettingsUpdate = async ({
    clinicId,
    body,
  }: {
    clinicId: number;
    body: any;
  }): Promise<any> => {
    return ChiroUpAPI.put('api', `/reporting/${clinicId}/scheduling/recall-settings`, { body });
  };

  const recallTemplateList = async ({
    clinicId,
  }: {
    clinicId: number;
  }): Promise<RecallMessageTemplate[]> => {
    return ChiroUpAPI.get('api', `/reporting/${clinicId}/scheduling/recall-templates`, {});
  };

  const recallTemplateCreate = async ({
    clinicId,
    body,
  }: {
    clinicId: number;
    body: Partial<RecallMessageTemplate>;
  }): Promise<RecallMessageTemplate[]> => {
    return ChiroUpAPI.post('api', `/reporting/${clinicId}/scheduling/recall-templates`, { body });
  };

  const recallTemplateUpdate = async ({
    clinicId,
    templateId,
    body,
  }: {
    clinicId: number;
    templateId: number;
    body: Partial<RecallMessageTemplate>;
  }): Promise<RecallMessageTemplate[]> => {
    return ChiroUpAPI.put(
      'api',
      `/reporting/${clinicId}/scheduling/recall-templates/${templateId}`,
      { body }
    );
  };

  const recallTemplateDelete = async ({
    clinicId,
    templateId,
  }: {
    clinicId: number;
    templateId: number;
  }): Promise<RecallMessageTemplate[]> => {
    return ChiroUpAPI.del(
      'api',
      `/reporting/${clinicId}/scheduling/recall-templates/${templateId}`,
      {}
    );
  };

  // Packages, Memberships & Recurring report (12375541198). Arrays serialize
  // comma-joined through ChiroUpAPI, matching the backend's split(',').
  const packagesMembershipsQueryParams = (params: Record<string, unknown>) => {
    const queryParams: Record<string, unknown> = { ...params };

    Object.keys(queryParams).forEach(key => {
      const val = queryParams[key];

      if (
        val === null ||
        val === undefined ||
        val === false ||
        (Array.isArray(val) && !val.length)
      ) {
        delete queryParams[key];
      }
    });

    return queryParams;
  };

  const packagesReport = async ({
    params,
    clinicId,
  }: {
    params: PackagesReportFilters;
    clinicId: number;
  }): Promise<any> => {
    return ChiroUpAPI.get('api', `/reporting/${clinicId}/billing/packages-memberships/packages`, {
      queryParams: packagesMembershipsQueryParams(params),
    });
  };

  const membershipsReport = async ({
    params,
    clinicId,
  }: {
    params: MembershipsReportFilters;
    clinicId: number;
  }): Promise<any> => {
    return ChiroUpAPI.get(
      'api',
      `/reporting/${clinicId}/billing/packages-memberships/memberships`,
      { queryParams: packagesMembershipsQueryParams(params) }
    );
  };

  const recurringSchedulesReport = async ({
    params,
    clinicId,
  }: {
    params: RecurringSchedulesReportFilters;
    clinicId: number;
  }): Promise<any> => {
    return ChiroUpAPI.get('api', `/reporting/${clinicId}/billing/packages-memberships/recurring`, {
      queryParams: packagesMembershipsQueryParams(params),
    });
  };

  return {
    listRegionConditions,
    listConditions,
    getConditionDetails,
    saveReleaseReport,
    list,
    arAging,
    arAgingTopPatients,
    arAgingTopPayors,
    discountDetail,
    purchaseDetailReport,
    postedPayments,
    paymentCollection,
    patientCredits,
    packagesReport,
    membershipsReport,
    recurringSchedulesReport,
    claimRejections,
    claimRejectionsDownload,
    claimRejectionWorked,
    scheduledPatients,
    unscheduledPatients,
    newPatients,
    unsignedSOAPNotes,
    verifyPayments,
    unVerifyPayments,
    paymentCollectionSnapshotList,
    paymentCollectionSnapshotGenerate,
    paymentCollectionSnapshotLoad,
    paymentCollectionSnapshotDownload,
    compensationCalculator,
    patientSummary,
    insurancePolicy,
    serviceCodeUsage,
    supplementSupplySales,
    supplementSupplySalesDownload,
    supplementSupplySalesSnapshotList,
    supplementSupplySalesSnapshotGenerate,
    supplementSupplySalesSnapshotLoad,
    supplementSupplySalesSnapshotDownload,
    downloadAging,
    fetchArAgingInvoices,
    recallBoard,
    recallContactHistory,
    recallPatientGet,
    recallPatientUpdate,
    recallAttemptCreate,
    recallMoveToReactivation,
    recallRemove,
    recallNoHistoryList,
    recallNoHistoryAddToRecall,
    recallNoHistoryRemove,
    recallSettingsGet,
    recallSettingsUpdate,
    recallTemplateList,
    recallTemplateCreate,
    recallTemplateUpdate,
    recallTemplateDelete,
  };
};

export default reportService();
